// resources/js/admin/components/shared/media/MediaUploader.tsx

import { useState, useRef } from "react";
import { Upload, X, File, Image, Video } from "lucide-react";

interface MediaUploaderProps {
    siteSlug: string;
    onUpload: (file: File, altText?: string) => Promise<void>;
    isLoading?: boolean;
    accept?: string;
    multiple?: boolean;
}

export default function MediaUploader({
    siteSlug,
    onUpload,
    isLoading = false,
    accept = "image/*,video/*,application/*",
    multiple = false,
}: MediaUploaderProps) {
    const [dragActive, setDragActive] = useState(false);
    const [files, setFiles] = useState<File[]>([]);
    const [altText, setAltText] = useState("");
    const [previews, setPreviews] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const newFiles = Array.from(e.dataTransfer.files);
            setFiles((prev) => [...prev, ...newFiles]);
            
            // Générer les previews
            const newPreviews = newFiles.map((file) => URL.createObjectURL(file));
            setPreviews((prev) => [...prev, ...newPreviews]);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const newFiles = Array.from(e.target.files);
            setFiles((prev) => [...prev, ...newFiles]);
            
            // Générer les previews
            const newPreviews = newFiles.map((file) => URL.createObjectURL(file));
            setPreviews((prev) => [...prev, ...newPreviews]);
        }
    };

    const removeFile = (index: number) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
        // Libérer l'URL de preview
        URL.revokeObjectURL(previews[index]);
        setPreviews((prev) => prev.filter((_, i) => i !== index));
        if (files.length === 1) {
            setAltText("");
        }
    };

    const handleUpload = async () => {
        for (const file of files) {
            await onUpload(file, altText || undefined);
        }
        // Nettoyer les previews
        previews.forEach((preview) => URL.revokeObjectURL(preview));
        setFiles([]);
        setPreviews([]);
        setAltText("");
    };

    const getFileIcon = (file: File) => {
        if (file.type.startsWith('image/')) return Image;
        if (file.type.startsWith('video/')) return Video;
        return File;
    };

    const isImage = (file: File) => file.type.startsWith('image/');
    const isVideo = (file: File) => file.type.startsWith('video/');

    return (
        <div className="space-y-4">
            {/* Zone de drop */}
            <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                    dragActive
                        ? "border-primary bg-primary/5"
                        : "border-gray-300 hover:border-primary/50"
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
            >
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">
                    Glissez vos fichiers ici ou
                </p>
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                    Parcourir
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={accept}
                    multiple={multiple}
                    onChange={handleFileSelect}
                    className="hidden"
                />
                <p className="text-xs text-gray-400 mt-2">
                    Max 10MB par fichier
                </p>
            </div>

            {/* Champ alt text */}
            {files.length > 0 && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Texte alternatif (pour tous les fichiers)
                    </label>
                    <input
                        type="text"
                        value={altText}
                        onChange={(e) => setAltText(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                        placeholder="Description des fichiers"
                    />
                </div>
            )}

            {/* Liste des fichiers sélectionnés avec preview */}
            {files.length > 0 && (
                <div className="space-y-2">
                    <h4 className="font-medium text-gray-700">
                        Fichiers sélectionnés ({files.length})
                    </h4>
                    {files.map((file, index) => {
                        const Icon = getFileIcon(file);
                        return (
                            <div
                                key={index}
                                className="flex items-center gap-3 bg-gray-50 rounded-lg p-3"
                            >
                                {/* Preview */}
                                <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center">
                                    {isImage(file) && previews[index] ? (
                                        <img
                                            src={previews[index]}
                                            alt={file.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : isVideo(file) && previews[index] ? (
                                        <video
                                            src={previews[index]}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <Icon className="w-8 h-8 text-gray-400" />
                                    )}
                                </div>

                                {/* Infos fichier */}
                                <div className="flex-1 min-w-0">
                                    <span className="text-sm font-medium text-gray-700 truncate block">
                                        {file.name}
                                    </span>
                                    <span className="text-xs text-gray-400">
                                        {(file.size / 1024).toFixed(1)} KB
                                    </span>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => removeFile(index)}
                                    className="text-gray-400 hover:text-red-600 transition-colors flex-shrink-0"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Bouton d'upload */}
            {files.length > 0 && (
                <button
                    type="button"
                    onClick={handleUpload}
                    disabled={isLoading}
                    className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                    {isLoading ? "Téléchargement..." : `Télécharger ${files.length} fichier(s)`}
                </button>
            )}
        </div>
    );
}